import React from 'react';
import { useIntl } from 'react-intl';

import { CustomerSearchRow } from '../../../../store/series/types';

import styles from './CustomerSearchSection.module.scss';

type SearchDropdownProps = {
  query: string;
  error: boolean;
  results: CustomerSearchRow[];
  onSelect: (row: CustomerSearchRow) => void;
  onCreate: () => void;
};

function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query) {
    return text;
  }

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const matchIndex = lowerText.indexOf(lowerQuery);
  if (matchIndex === -1) {
    return text;
  }

  const before = text.slice(0, matchIndex);
  const match = text.slice(matchIndex, matchIndex + query.length);
  const after = text.slice(matchIndex + query.length);

  return (
    <>
      {before}
      <mark className={styles.highlight}>{match}</mark>
      {after}
    </>
  );
}

function buildFullName(row: CustomerSearchRow): string {
  const parts: string[] = [];
  if (row.name) {
    parts.push(row.name);
  }
  if (row.lastName) {
    parts.push(row.lastName);
  }
  return parts.join(' ').trim();
}

function SearchDropdownComponent({ query, error, results, onSelect, onCreate }: SearchDropdownProps) {
  const intl = useIntl();

  if (error) {
    return (
      <div className={styles.dropdown} data-testid="series-customer-search-dropdown">
        <div className={styles.dropdownStatus} data-testid="series-customer-search-error">
          {intl.formatMessage({ id: 'common.defaultError' })}
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className={styles.dropdown} data-testid="series-customer-search-dropdown">
        <div className={styles.dropdownEmpty} data-testid="series-customer-search-empty">
          <div className={styles.dropdownStatus}>
            {intl.formatMessage({ id: 'seriesbooking.booking.noCustomersFound' })}
          </div>
          <button
            type="button"
            className={styles.addCustomerLink}
            data-testid="series-customer-search-empty-create"
            onClick={onCreate}
          >
            {intl.formatMessage({ id: 'seriesbooking.booking.createNewCustomer' })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dropdown} data-testid="series-customer-search-dropdown">
      {results.map((row) => {
        const fullName = buildFullName(row);

        return (
          <button
            type="button"
            key={row.id}
            className={styles.dropdownRow}
            data-testid={`series-customer-search-row-${row.id}`}
            onClick={() => onSelect(row)}
          >
            <div className={styles.dropdownRowMain}>
              <span className={styles.dropdownRowName}>
                {highlightMatch(fullName, query)}
              </span>
              {row.hasActiveSeries && (
                <span className={styles.openPill}>
                  {intl.formatMessage({ id: 'seriesbooking.detail.badge.open' })}
                </span>
              )}
            </div>
            <div className={styles.dropdownRowMeta}>
              {row.phoneNumber && <span>{row.phoneNumber}</span>}
              {row.phoneNumber && row.email && <span> · </span>}
              {row.email && <span>{row.email}</span>}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export const SearchDropdown = React.memo(SearchDropdownComponent);
